import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import OrganisationSettings from './OrganisationSettings'

function OrgManagement({ session, profile, onNavigate, orgUsers }) {
  const [orgs, setOrgs] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)
  const [newOrg, setNewOrg] = useState({ name: '', slug: '', licence_tier: 'starter', licensed_user_count: 5 })

  useEffect(() => { loadOrgs() }, [])

  const loadOrgs = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('organisations')
      .select('id, name, slug, primary_colour, licence_tier, licensed_user_count, licence_active, ai_enabled, org_profile, created_at')
      .order('name')
    setOrgs(data || [])
    setLoading(false)
  }

  const handleCreate = async () => {
    if (!newOrg.name.trim() || !newOrg.slug.trim()) return
    setCreating(true); setError(null)
    const { data, error } = await supabase
      .from('organisations')
      .insert([{
        name: newOrg.name.trim(),
        slug: newOrg.slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-'),
        licence_tier: newOrg.licence_tier,
        licensed_user_count: parseInt(newOrg.licensed_user_count),
        licence_active: true,
        primary_colour: '1A3A2A',
        secondary_colour: '2D5A3D',
      }])
      .select()
      .single()
    if (error) { setError(error.message); setCreating(false); return }
    setOrgs(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)))
    setNewOrg({ name: '', slug: '', licence_tier: 'starter', licensed_user_count: 5 })
    setShowCreate(false)
    setCreating(false)
    setSelectedOrg(data)
  }

  const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

  if (selectedOrg) {
    return (
      <OrganisationSettings
        session={session}
        profile={profile}
        onNavigate={onNavigate}
        orgId={selectedOrg.id}
        orgName={selectedOrg.name}
        orgUsers={orgUsers}
        onSettingsSaved={loadOrgs}
        onBack={() => setSelectedOrg(null)}
      />
    )
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      <div className="page-header">
        <p className="label">Super Admin</p>
        <h1>Organisation Management</h1>
        <p>Create and manage all organisations on the Cadence platform.</p>
      </div>

      {error && <div className="error-msg">{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-dark)' }}>
          {orgs.length} Organisation{orgs.length !== 1 ? 's' : ''}
        </div>
        <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
          + New Organisation
        </button>
      </div>

      {showCreate && (
        <div style={{ background: 'var(--white)', border: '1px solid var(--green-light)', borderLeft: '4px solid var(--green-dark)', padding: '20px 24px', marginBottom: '20px' }}>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.25rem', fontWeight: 600, color: 'var(--green-dark)', marginBottom: '16px' }}>New Organisation</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Organisation Name</label>
              <input
                type="text"
                value={newOrg.name}
                onChange={e => setNewOrg(p => ({ ...p, name: e.target.value, slug: slugify(e.target.value) }))}
                placeholder="e.g. Acme Defence Ltd"
                autoFocus
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Slug (URL identifier)</label>
              <input
                type="text"
                value={newOrg.slug}
                onChange={e => setNewOrg(p => ({ ...p, slug: e.target.value }))}
                placeholder="e.g. acme-defence"
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Licence Tier</label>
              <select value={newOrg.licence_tier} onChange={e => setNewOrg(p => ({ ...p, licence_tier: e.target.value }))}>
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Licensed User Count</label>
              <input
                type="number"
                value={newOrg.licensed_user_count}
                onChange={e => setNewOrg(p => ({ ...p, licensed_user_count: e.target.value }))}
                min="5"
              />
            </div>
          </div>
          <div className="actions" style={{ marginTop: '16px' }}>
            <button className="btn btn--primary" onClick={handleCreate} disabled={creating || !newOrg.name.trim()}>
              {creating ? 'Creating...' : 'Create Organisation'}
            </button>
            <button className="btn btn--secondary" onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading && <p style={{ color: 'var(--slate)' }}>Loading organisations...</p>}

      {!loading && orgs.map(org => {
        const profile_data = org.org_profile || {}
        const profileComplete = !!(profile_data.sectors?.length && profile_data.capabilities?.length && profile_data.strengths)
        return (
          <div
            key={org.id}
            onClick={() => setSelectedOrg(org)}
            style={{ background: 'var(--white)', border: '1px solid var(--green-light)', borderLeft: '4px solid #' + (org.primary_colour || '1A3A2A'), marginBottom: '8px', padding: '16px 20px', cursor: 'pointer', transition: 'all 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--white)'}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.125rem', fontWeight: 600, color: 'var(--green-dark)' }}>{org.name}</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--slate)', marginTop: '2px' }}>
                  {org.slug} · {org.licence_tier} · {org.licensed_user_count} seats
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {org.ai_enabled && (
                  <span style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green)', background: 'var(--green-pale)', padding: '2px 8px', border: '1px solid var(--green)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>AI On</span>
                )}
                {profileComplete ? (
                  <span style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--green)', background: 'var(--green-pale)', padding: '2px 8px', border: '1px solid var(--green)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Profile ✓</span>
                ) : (
                  <span style={{ fontSize: '0.625rem', fontWeight: 700, color: '#E67E22', background: '#FEF3E2', padding: '2px 8px', border: '1px solid #E67E22', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Profile incomplete</span>
                )}
                <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>→</span>
              </div>
            </div>
          </div>
        )
      })}

      <div className="actions" style={{ marginTop: '24px' }}>
        <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>← Dashboard</button>
      </div>
    </div>
  )
}

export default OrgManagement
